import { i18n } from "../i18n/i18n";

import React, { Component } from 'react'
import { produce } from 'immer'
import { compact, difference, first, includes, isEmpty, map } from 'lodash'
import alertService from '../services/alert.service'
import confirmService from '../services/confirm.service'
import geolocationService from '../services/geolocation.service'
import palletApi from '../apis/pallet.api'
import inventoryCaseApi, { CaseAssignRequest, CaseData } from '../apis/inventoryCase.api'
import { ConfirmParams } from '../components/Confirm/Confirm.state'
import Card from '../components/Card'
import CardButton from '../components/CardButton'
import ContentLayout from '../components/ContentLayout'
import ContentMain from '../components/ContentMain'
import PageHeading from '../components/PageHeading'
import CasePicker from '../components/CasePicker'
import InputScan from '../components/InputScan'

interface CaseAssignmentProps {}

interface CaseAssignmentState {
  palletCode: string
  caseCode: string
  selectedCases: CaseData[]
  cpp: number
  unusedPositions: number[],
  latitude: number | null
  longitude: number | null
}

export default class CaseAssignment
  extends Component<CaseAssignmentProps, CaseAssignmentState> {
  constructor (props: CaseAssignmentProps) {
    super(props)
    this.state = {
      palletCode: '',
      caseCode: '',
      selectedCases: [],
      cpp: 0,
      unusedPositions: [],
      latitude: null,
      longitude: null
    }
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleInputScan = this.handleInputScan.bind(this)
    this.handleAssignClick = this.handleAssignClick.bind(this)
  }

  get selectedCaseCodes () {
    return map(this.state.selectedCases, selectedCase => {
      return selectedCase ? selectedCase.code : null
    })
  }

  get selectedPositions () {
    const caseCodes = map(this.selectedCaseCodes, (caseCode, index) => {
      return caseCode ? index : null
    })
    return compact(caseCodes)
  }

  get nextUnusedPosition () {
    const { unusedPositions } = this.state
    const unassignedPositions = difference(unusedPositions, this.selectedPositions)
    return first(unassignedPositions)
  }

  isCaseScanned (code: string) {
    return includes(this.selectedCaseCodes, code)
  }

  readPalletByCode (code: string) {
    palletApi.readByCode(code, pallet => {
      if (!pallet || !pallet.item || !pallet.unusedPositions) return
      this.setState({
        cpp: pallet.item.cpp,
        unusedPositions: pallet.unusedPositions
      })
    })
  }

  readCaseByCode (code: string) {
    inventoryCaseApi.readByCode(code, inventoryCase => {
      if (!inventoryCase) return
      const selectedCases = produce(this.state.selectedCases, selectedCases => {
        if (this.nextUnusedPosition) {
          selectedCases[this.nextUnusedPosition] = inventoryCase
        }
      })
      this.setState({ selectedCases, caseCode: '' })
    })
  }

  reset () {
    this.setState({
      palletCode: '',
      caseCode: '',
      selectedCases: [],
      cpp: 0
    })
  }

  handleInputChange (name: string, value: string) {
    if (name === 'palletCode') {
      this.setState({ palletCode: value, selectedCases: [], cpp: 0 })
    } else if (name === 'caseCode') {
      this.setState({ caseCode: value })
    }
  }

  handleInputScan (name: string, value: string) {
    if (!value) return
    if (name === 'palletCode') {
      this.readPalletByCode(value)
    } else if (name === 'caseCode') {
      if (this.isCaseScanned(value)) {
        alertService.info('Already applied')
        this.setState({ caseCode: '' })
      } else if (isEmpty(this.state.unusedPositions)) {
        alertService.info('No space available')
      } else {
        this.readCaseByCode(value)
      }
    }
  }

  assign () {
    const { palletCode, latitude, longitude } = this.state
    const payload: CaseAssignRequest = {}
    if (palletCode) payload.palletCode = palletCode
    if (this.selectedCaseCodes) payload.cases = compact(this.selectedCaseCodes)
    if (latitude) payload.latitude = latitude
    if (longitude) payload.longitude = longitude
    inventoryCaseApi.assign(payload, cases => {
      if (!cases) return
      alertService.success('Successfully assigned')
      this.reset()
    })
  }

  handleAssignClick () {
    const confirm: ConfirmParams = {
      id: 1,
      question: i18n('Please review and confirm'),
      confirmText: i18n('Confirm'),
      cancelText: i18n('Cancel'),
      onConfirm: () => this.assign()
    }
    confirmService.push(confirm)
  }

  readGeolocation () {
    geolocationService.access(coordinate => {
      if (!coordinate) return
      const { latitude, longitude } = coordinate
      this.setState({ latitude, longitude })
    })
  }

  componentDidMount () {
    this.readGeolocation()
  }

  render () {
    return (
      <ContentLayout>
        <ContentMain layout="twoThird">
          <Card>
            <PageHeading
              text={i18n("Case Assignment")}
              doc='cases-case-assignment' />
            <InputScan
              name="palletCode"
              value={this.state.palletCode}
              label={i18n("Scan pallet code")}
              autofocus={true}
              onChange={this.handleInputChange}
              onScan={this.handleInputScan} />
            <CasePicker
              cases={this.state.cpp}
              availables={this.state.unusedPositions}
              selectedPositions={this.selectedPositions}
              selectedCases={this.state.selectedCases} />
            <InputScan
              name="caseCode"
              value={this.state.caseCode}
              label={i18n("Scan case code")}
              onChange={this.handleInputChange}
              onScan={this.handleInputScan} />
            <CardButton
              proceedText={i18n("Assign")}
              onProceedClick={this.handleAssignClick} />
          </Card>
        </ContentMain>
      </ContentLayout>
    )
  }
}
